import React, { useEffect, useState, useMemo, useCallback } from "react";
import Context from "../../Context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";
import { isEmpty } from "ramda";
import { MdSearch, MdClear } from "react-icons/md";
import { FiInbox, FiAlertCircle, FiExternalLink } from "react-icons/fi";
import { HiRefresh } from "react-icons/hi";

import { Spinner } from "../../components/ui";

export const LotteriesNotOnBlock = () => {
  const { callApi, configs } = Context();
  const [booksNotOnBlock, setBooksNotOnBlock] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBookIds, setSelectedBookIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);

  // Fetch initial books not on block
  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await callApi(
        "lotteries-countImports",
        { isOnBlock: false, isSold: false, roundDate: configs?.roundDate },
        { notShowLoading: true }
      );
      const data = result?.data || {};
      setBooksNotOnBlock(data);
    } catch (error) {
      console.error("Fetch lotteries not on block failed:", error);
    } finally {
      setLoading(false);
    }
  }, [callApi, configs?.roundDate]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Client-side filtering
  const filteredEntries = useMemo(() => {
    const entries = Object.entries(booksNotOnBlock);
    const keyword = searchTerm.trim().toLowerCase();

    const filtered = keyword
      ? entries.filter(([key, val]) => {
        const matchKey = key.toLowerCase().includes(keyword);
        const matchUploader = (val?.uploader || "")
          .toLowerCase()
          .includes(keyword);
        return matchKey || matchUploader;
      })
      : entries;

    return filtered.sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true })
    );
  }, [booksNotOnBlock, searchTerm]);

  // Total summary of current view
  const totalBooks = useMemo(
    () => filteredEntries.filter(([, x]) => (Number(x?.size) || 0) > 0).length,
    [filteredEntries]
  );

  const totalTickets = useMemo(
    () =>
      filteredEntries.reduce((acc, [, x]) => acc + (Number(x?.size) || 0), 0),
    [filteredEntries]
  );

  // Summary of selected items
  const { selectedBooksCount, selectedTicketsCount } = useMemo(() => {
    let tickets = 0;
    const selectedSet = new Set(selectedBookIds);

    Object.values(booksNotOnBlock).forEach((val) => {
      if (selectedSet.has(val.createdRawAt)) {
        tickets += Number(val.size) || 0;
      }
    });

    return {
      selectedBooksCount: selectedBookIds.length,
      selectedTicketsCount: tickets,
    };
  }, [selectedBookIds, booksNotOnBlock]);

  // Check if all visible items are selected
  const isAllVisibleSelected = useMemo(() => {
    if (filteredEntries.length === 0) return false;
    return filteredEntries.every(([, val]) =>
      selectedBookIds.includes(val.createdRawAt)
    );
  }, [filteredEntries, selectedBookIds]);

  // Toggle select all (only affects visible items in current filter)
  const toggleSelectAll = () => {
    const visibleCreatedRawAts = filteredEntries.map(
      ([, val]) => val.createdRawAt
    );

    if (isAllVisibleSelected) {
      setSelectedBookIds((prev) =>
        prev.filter((id) => !visibleCreatedRawAts.includes(id))
      );
    } else {
      setSelectedBookIds((prev) =>
        Array.from(new Set([...prev, ...visibleCreatedRawAts]))
      );
    }
  };

  const toggleSelect = (createdRawAt) => {
    setSelectedBookIds((prev) =>
      prev.includes(createdRawAt)
        ? prev.filter((x) => x !== createdRawAt)
        : [...prev, createdRawAt]
    );
  };

  // Push selected books to block
  const handlePushToBlock = async (isMagicNumber) => {
    if (isEmpty(selectedBookIds) || pushing) return;
    const typeLabel = isMagicNumber ? "เลขบวงสรวง" : "ปกติ";

    const confirmMsg = `ยืนยันการนำสลาก ${selectedBooksCount} เล่ม (${selectedTicketsCount.toLocaleString()} ใบ) เข้าสู่ระบบวางขาย (${typeLabel}) หรือไม่?`;
    if (!window.confirm(confirmMsg)) return;

    setPushing(true);
    try {
      await callApi("blocks-pushToBlock", {
        isActive: true,
        createdAts: selectedBookIds,
        isMagicNumber,
      });
      setSelectedBookIds([]);
      await getData();
    } catch (err) {
      console.error("handlePushToBlock failed:", err);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-12">
      <Header
        header="สลากในระบบหลังบ้าน"
        subHeader="รายการเล่มที่นำเข้าแล้ว แต่ยังไม่ได้ปล่อยวางขายบนแผง"
        icon={<FiInbox className="text-navy text-2xl" />}
      />

      {/* Action and Summary Bar */}
      <div className="mt-8 mb-4 flex flex-wrap items-center justify-between gap-4">
        {/* Buttons for publishing */}
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm py-2.5 px-4 rounded-md shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={isEmpty(selectedBookIds) || loading || pushing}
            onClick={() => handlePushToBlock(false)}
          >
            {pushing ? (
              <Spinner color="border-white" />
            ) : null}
            <span>
              วางขายปกติ{" "}
              {selectedBooksCount > 0
                ? `${selectedBooksCount} เล่ม (${selectedTicketsCount.toLocaleString()} ใบ)`
                : ""}
            </span>
          </button>

          <button
            className="flex items-center gap-2 bg-blue hover:opacity-85 text-white font-bold text-sm py-2.5 px-4 rounded-md shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={isEmpty(selectedBookIds) || loading || pushing}
            onClick={() => handlePushToBlock(true)}
          >
            {pushing ? (
              <Spinner className="w-4 h-4" color="border-white" />
            ) : null}
            <span>
              วางขายเลขบวงสรวง{" "}
              {selectedBooksCount > 0
                ? `${selectedBooksCount} เล่ม (${selectedTicketsCount.toLocaleString()} ใบ)`
                : ""}
            </span>
          </button>
        </div>

        {/* Selected pill indicator */}
        {selectedBooksCount > 0 && (
          <div className="text-xs font-semibold px-3 py-1.5 bg-blue/10 text-blue border border-blue/20 rounded-md flex items-center gap-2 animate-in fade-in">
            <span>เลือกอยู่: {selectedBooksCount} เล่ม</span>
            <span className="text-gray-400">|</span>
            <span>{selectedTicketsCount.toLocaleString()} ใบ</span>
            <button
              onClick={() => setSelectedBookIds([])}
              className="ml-1 text-gray-500 hover:text-red-500 font-bold"
              title="ล้างรายการที่เลือก"
            >
              ×
            </button>
          </div>
        )}
      </div>

      <div className="relative bg-white w-full rounded-md shadow-sm border border-[#E5E7E9] overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-wrap justify-between items-center border-b border-[#E5E7E9] px-4 py-3 bg-gray-50/60 gap-4">
          <div className="flex items-center gap-3">
            <p className="font-bold text-sm text-gray-800">
              สลากหลังบ้านทั้งหมด
            </p>
            <span className="text-xs font-bold px-2.5 py-1 bg-gray-200/80 text-gray-700 rounded-md">
              {totalBooks.toLocaleString()} เล่ม (
              {totalTickets.toLocaleString()} ใบ)
            </span>
            <button
              className="p-1.5 bg-white border border-gray-300 text-gray-700 text-base rounded-md hover:bg-gray-100 transition shadow-sm"
              onClick={getData}
              disabled={loading || pushing}
              title="รีเฟรชข้อมูล"
            >
              <HiRefresh className={loading ? "animate-spin" : ""} />
            </button>
          </div>

          {/* Search Box */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                className="border border-gray-300 focus:border-blue py-1.5 pl-3 pr-8 w-64 rounded-md text-sm font-semibold placeholder:text-gray-400 focus:outline-none"
                type="text"
                placeholder="ค้นหาชื่อเล่ม (เช่น 0000-100)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <MdClear className="w-4 h-4" />
                </button>
              ) : (
                <MdSearch className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              )}
            </div>
            {searchTerm && (
              <span className="text-xs text-gray-500 font-semibold">
                พบ {filteredEntries.length} เล่ม
              </span>
            )}
          </div>
        </div>

        {/* Table Section */}
        <div className="relative min-h-[450px]">
          {loading && (
            <div className="absolute inset-0 flex flex-col justify-center items-center bg-white/70 backdrop-blur-[1px] z-20">
              <Spinner className="w-8 h-8" />
              <span className="mt-2 text-xs font-semibold text-navy">
                กำลังโหลดข้อมูล...
              </span>
            </div>
          )}

          <Table>
            <TableHeader className="bg-[#f8f9fa] border-b border-gray-200">
              <TableRow>
                <TableHead className="text-center py-3 w-32">
                  <button
                    className={`font-semibold text-white py-1.5 px-3 rounded-md text-xs transition duration-150 ${isAllVisibleSelected
                        ? "bg-failed hover:opacity-85"
                        : "bg-navy hover:opacity-85"
                      }`}
                    onClick={toggleSelectAll}
                    disabled={filteredEntries.length === 0}
                  >
                    {isAllVisibleSelected ? "ยกเลิกที่เลือก" : "เลือกทั้งหมด"}
                  </button>
                </TableHead>
                <TableHead className="text-center w-14 text-xs font-bold">
                  ลำดับ
                </TableHead>
                <TableHead className="w-24 text-center text-xs font-bold">
                  ตรวจสอบ
                </TableHead>
                <TableHead className="text-center text-xs font-bold">
                  วันที่-เวลานำเข้า
                </TableHead>
                <TableHead className="text-xs font-bold">ชื่อเล่ม (Zip)</TableHead>
                <TableHead className="text-center text-xs font-bold">
                  จำนวนสลาก
                </TableHead>
                <TableHead className="text-center text-xs font-bold">
                  ผู้นำเข้า
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="min-h-60 flex flex-col justify-center items-center text-gray-400 font-medium gap-2">
                      <FiInbox className="w-8 h-8 text-gray-300" />
                      <span>
                        {searchTerm
                          ? `ไม่พบเล่มสลากที่ตรงกับ "${searchTerm}"`
                          : "ไม่พบข้อมูลสลากหลังบ้านในงวดนี้"}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntries.map(([key, value], index) => {
                  const sizeNum = Number(value.size) || 0;
                  const isSelected = selectedBookIds.includes(
                    value.createdRawAt
                  );

                  return (
                    <TableRow
                      key={value.createdRawAt}
                      className={`hover:bg-blue/5 transition-colors ${isSelected ? "bg-blue/5" : ""
                        }`}
                    >
                      {/* Checkbox */}
                      <TableCell className="text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue rounded border-gray-300 focus:ring-blue cursor-pointer"
                          checked={isSelected}
                          onChange={() => toggleSelect(value.createdRawAt)}
                        />
                      </TableCell>

                      {/* Index */}
                      <TableCell className="text-center font-mono text-xs text-gray-500">
                        {index + 1}
                      </TableCell>

                      {/* Detail Link */}
                      <TableCell className="text-center">
                        <button
                          className="inline-flex items-center gap-1 bg-white border border-gray-300 text-gray-700 hover:bg-blue hover:text-white hover:border-blue text-xs font-semibold py-1.5 px-2.5 rounded-md transition duration-150 shadow-sm"
                          onClick={() =>
                            window.open(
                              `/lotteries/detail?fileNameId=${encodeURIComponent(
                                key
                              )}`,
                              "_blank"
                            )
                          }
                          title="ดูสลากทุกใบในเล่มนี้"
                        >
                          <span>ดูสลาก</span>
                          <FiExternalLink className="w-3 h-3" />
                        </button>
                      </TableCell>

                      {/* Date */}
                      <TableCell className="text-center text-sm text-gray-600">
                        {new Date(Number(value.createdRawAt)).toLocaleDateString(
                          "th-TH",
                          {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                          }
                        )}{" "}
                        น.
                      </TableCell>

                      {/* Book Name */}
                      <TableCell className="font-semibold text-sm text-gray-800">
                        {key}
                      </TableCell>

                      {/* Ticket Count & Health Indicator */}
                      <TableCell className="text-center">
                        <div className="inline-flex items-center justify-center gap-1.5">
                          {sizeNum === 100 ? (
                            <span className="font-bold text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-md">
                              100 ใบ
                            </span>
                          ) : sizeNum > 100 ? (
                            <span
                              className="font-bold text-sm text-blue bg-blue/10 border border-blue/20 px-2.5 py-0.5 rounded-md"
                              title={`สลากเล่มนี้มี ${sizeNum} ใบ (มากกว่า 100)`}
                            >
                              {sizeNum} ใบ
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 font-bold text-xs text-amber-800 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-md"
                              title="สลากเล่มนี้มีน้อยกว่า 100 ใบ โปรดตรวจสอบก่อนวางขาย"
                            >
                              <FiAlertCircle className="text-amber-600 w-3.5 h-3.5" />
                              {sizeNum} ใบ (&lt; 100)
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Uploader */}
                      <TableCell className="text-center text-sm text-gray-600">
                        {Array.isArray(value.uploader)
                          ? value.uploader.join(", ")
                          : value.uploader || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default LotteriesNotOnBlock;
